import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";

import { URLS, SAGA_ACTIONS, EXTERNAL_AE_URL, EXTERNAL_URL } from "src/_config";
import SweetAlert from "react-bootstrap-sweetalert";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";

interface Job {
  emp_job_id: string;
  job_hash: string;
  job_title: string;
  city_name?: string;
  country_name?: string;
  posted_date: string;
  expiry_date: string;
  status: number;
  job_type?: string;
  applicants?: number;
  newApplicants?: number;
  shortlisted?: number;
  finalDecision?: number;
}

interface WorkPlace {
  id: number;
  name: string;
  description: string;
  status: number;
}

export default function JobListingsSection() {
  const { t } = useTranslation("dashboard");
  const dispatch = useDispatch();
  const jobListings = useJobListSelector();
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const toast = useToaster();

  console.log(userPermissions);
  // Redux selector for workplaces
  const { workplaces } = useSelector((state: any) => ({
    workplaces: state.global.workplaces,
  }));

  const workplacesData = workplaces && workplaces.length > 0 ? workplaces : null;

  const history = useHistory();
  const { callUpdateJobStatus, callCloneJob, callGetEmployerJobs } = useEmployerJobApi();
  const [searchPost, setSearchPost] = useState("");
  const [workplaceType, setWorkplaceType] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [showCloneConfirm, setShowCloneConfirm] = useState(false);
  const [showUpdateStatusConfirm, setShowUpdateStatusConfirm] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const itemsPerPage = 10; // One item per page as requested

  // Fetch workplaces data on component mount
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.WORK_PLACES,
      payload: { limit: 30, offset: 0 },
    });
  }, [dispatch]);

  // Helper function to map job status
  const getJobStatus = (status: number) => {
    switch (status) {
      case 0:
        return { text: t("dashboard.jobListings.table.status.inactive"), class: "status-inactive" };
      case 1:
        return { text: t("dashboard.jobListings.table.status.live"), class: "status-active" };
      case 2:
        return { text: t("dashboard.jobListings.table.status.expired"), class: "status-expired" };
      case 3:
        return { text: t("dashboard.jobListings.table.status.archived"), class: "status-archived" };
      case 9:
        return { text: t("dashboard.jobListings.table.status.pendingApproval"), class: "status-pending" };
      case 10:
        return { text: t("dashboard.jobListings.table.status.aiFiltering"), class: "status-filtering" };
      default:
        return { text: t("dashboard.jobListings.table.status.unknown"), class: "status-unknown" };
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Helper function to calculate days until expiry
  const getDaysUntilExpiry = (expiryDate: string) => {
    if (!expiryDate) return "";
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return t("dashboard.jobListings.table.expiry.expired");
    if (diffDays === 0) return t("dashboard.jobListings.table.expiry.expiresToday");
    return t("dashboard.jobListings.table.expiry.expiresIn", { days: diffDays });
  };

  // Pagination calculations
  const jobListingsArray = jobListings || [];
  const totalPages = Math.ceil(jobListingsArray.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = jobListingsArray.slice(startIndex, endIndex);

  console.log("Job Listings:", jobListingsArray);

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadJobPosting = canAccessMenu("read_my_jobs");
  const canEditJobPosting = canAccessMenu("edit_my_jobs");
  const canDeleteJobPosting = canAccessMenu("delete_my_jobs");

  const canEditAIPosting = canAccessMenu("edit_filter");
  const canDeleteAIPosting = canAccessMenu("delete_filter");

  // Function to refresh job listings
  const refreshJobListings = () => {
    // Get employer ID from user details
    const employerId = userDetails?.user?.company_id;

    if (!employerId) {
      console.error("Employer ID not found in user details");
      return;
    }

    const onSuccess = (message: string, data: any) => {
      // Job listings will be automatically updated in Redux store
      console.log("Job listings refreshed successfully");
    };

    const onError = (message: string, error: any) => {
      console.error("Failed to refresh job listings:", error);
    };

    // Always include job_status array as default parameter
    callGetEmployerJobs(employerId, { job_status: ["ai_filter", "open", "rejected"] }, onSuccess, onError);
  };

  // Refresh job listings on component mount
  useEffect(() => {
    refreshJobListings();
  }, []);

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === "member" && !canReadJobPosting) {
    return null;
  }
  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Function to handle search with filters
  const handleSearch = () => {
    // Get employer ID from user details
    const employerId = userDetails?.user?.id;

    if (!employerId) {
      console.error("Employer ID not found in user details");
      return;
    }

    // Validate date range
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toast.error("From date cannot be later than To date");
      return;
    }

    setIsSearching(true);

    // Build filter parameters
    const filterParams: any = {
      job_status: jobStatus ? [jobStatus] : ["ai_filter", "open", "rejected"], // Use selected status or default statuses
    };

    // Add workplace filter if selected
    if (workplaceType) {
      // Find the workplace ID by name
      const selectedWorkplace = workplacesData?.find((wp: WorkPlace) => wp.name === workplaceType);
      if (selectedWorkplace) {
        filterParams.workplace_type_id = selectedWorkplace.id;
      }
    }

    // Add search text filter if provided
    if (searchPost.trim()) {
      filterParams.search_text = searchPost.trim();
    }

    // Add date filters if provided
    if (fromDate) {
      filterParams["posted_date[from]"] = fromDate;
    }
    if (toDate) {
      filterParams["posted_date[to]"] = toDate;
    }

    console.log("Searching with filters:", filterParams);

    const onSuccess = (message: string, data: any) => {
      console.log("Job listings filtered successfully");
      setCurrentPage(1); // Reset to first page when searching
      setIsSearching(false);
      setIsFiltered(true);
    };

    const onError = (message: string, error: any) => {
      console.error("Failed to filter job listings:", error);
      toast.error("Failed to search jobs. Please try again.");
      setIsSearching(false);
    };

    // Call API with filter parameters
    callGetEmployerJobs(employerId, filterParams, onSuccess, onError);
  };

  // Function to clear filters and refresh
  const handleClearFilters = () => {
    setSearchPost("");
    setWorkplaceType("");
    setJobStatus("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    setIsFiltered(false);

    // Refresh with no filters
    refreshJobListings();
  };

  // Handle Enter key press in search input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handler to navigate to job details
  const handleJobClick = (jobId: string) => {
    const jobDetailsUrl = URLS.JOB_POSTING.JOB_DETAILS.replace(":jobId", jobId);
    history.push(jobDetailsUrl);
  };

  const handleEditJob = (jobId: any, jobStatus?: number) => {
    // If job status is 10 (AI filter), route to AI candidate search edit
    if (jobStatus === 10) {
      history.push(URLS.JOB_POSTING.AI_CANDIDATE_SEARCH_EDIT(jobId));
    } else {
      // Otherwise, route to regular job edit
      history.push(URLS.JOB_POSTING.EDIT(jobId));
    }
  };

  // Handler functions for action buttons
  const handleUpdateJobStatus = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowUpdateStatusConfirm(true);
  };

  const confirmUpdateJobStatus = () => {
    const onSuccess = (message: string, data: any) => {
      toast.success(message || "Job status updated successfully");
      // Refresh the job list after successful update
      refreshJobListings();
    };

    const onError = (message: string, error: any) => {
      toast.error(message || "Failed to update job status");
    };

    // Call the API with the job ID and any required data
    callUpdateJobStatus(
      selectedJobId,
      {
        // status: 0,
        status: 2,
      },
      onSuccess,
      onError
    ); // Example: block user (status 0 = inactive)

    setShowUpdateStatusConfirm(false);
    setSelectedJobId("");
  };

  const cancelUpdateJobStatus = () => {
    setShowUpdateStatusConfirm(false);
    setSelectedJobId("");
  };

  const handleCloneJob = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowCloneConfirm(true);
  };

  const confirmCloneJob = () => {
    const onSuccess = (message: string, data: any) => {
      toast.success(message || "Job reposted successfully");
      // Refresh the job list after successful repost
      refreshJobListings();
    };

    const onError = (message: string, error: any) => {
      toast.error(message || "Failed to repost job");
    };

    // Call the API with the job ID
    callCloneJob(selectedJobId, {}, onSuccess, onError);
    setShowCloneConfirm(false);
    setSelectedJobId("");
  };
  const cancelCloneJob = () => {
    setShowCloneConfirm(false);
    setSelectedJobId("");
  };

  return (
    <div className="job-listings-section">
      <div className="section-header">
        <h2 className="section-title">{t("dashboard.jobListings.title")}</h2>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.searchPost")}</label>
          <div className="dashboard-input-wrapper">
            <input
              type="text"
              placeholder={t("dashboard.jobListings.filters.searchPlaceholder")}
              value={searchPost}
              onChange={(e) => setSearchPost(e.target.value)}
              onKeyPress={handleKeyPress}
              className="filter-input"
            />
            <i className="dashboard-search-icon"></i>
          </div>
        </div>
        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.workplaceType")}</label>
          {/* Do Workplace Type Dropdown */}
          <select value={workplaceType} onChange={(e) => setWorkplaceType(e.target.value)} className="basic-select ">
            <option value="">{t("dashboard.jobListings.filters.selectOption")}</option>
            {workplacesData &&
              workplacesData.map((workplace: WorkPlace) => (
                <option key={workplace.id} value={workplace.name}>
                  {workplace.name}
                </option>
              ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.status")}</label>
          <select value={jobStatus} onChange={(e) => setJobStatus(e.target.value)} className="basic-select">
            <option value="">{t("dashboard.jobListings.filters.allStatuses")}</option>
            <option value="ai_filter">AI Filtering</option>
            <option value="open">Live</option>
            <option value="rejected">Rejected</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
            <option value="archived">Archived</option>
            <option value="pending_approval">Pending Approval</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.from")}</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="filter-input" />
        </div>
        <div className="filter-group">
          <label>{t("dashboard.jobListings.filters.to")}</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="filter-input" />
        </div>
        <div className="filter-buttons">
          <button className="find-btn" onClick={handleSearch} disabled={isSearching}>
            {isSearching
              ? t("dashboard.jobListings.filters.searching") || "Searching..."
              : t("dashboard.jobListings.filters.findBtn")}
          </button>
          <button className="find-btn btn-cancel" onClick={handleClearFilters} disabled={isSearching}>
            {t("dashboard.jobListings.filters.clear")}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      {/* {isFiltered && (
        <div className="search-results-summary">
          <p>
            {jobListingsArray.length === 0 
              ? t('dashboard.jobListings.results.noResults') || 'No jobs found with current filters'
              : t('dashboard.jobListings.results.found', { count: jobListingsArray.length }) || `Found ${jobListingsArray.length} job(s)`
            }
          </p>
        </div>
      )} */}

      {/* Job Listings Table */}
      <div className="table-container">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>{t("dashboard.jobListings.table.headers.jobTitle")}</th>
              <th>{t("dashboard.jobListings.table.headers.createdDate")}</th>
              <th>{t("dashboard.jobListings.table.headers.status")}</th>
              <th>{t("dashboard.jobListings.table.headers.workType")}</th>
              <th>{t("dashboard.jobListings.table.headers.applicants")}</th>
              <th>{t("dashboard.jobListings.table.headers.shortlisted")}</th>
              <th>{t("dashboard.jobListings.table.headers.finalDecision")}</th>
              <th>{t("dashboard.jobListings.table.headers.hired")}</th>
              <th>{t("dashboard.jobListings.table.headers.action")}</th>
            </tr>
          </thead>
          <tbody>
            {!currentPageData || currentPageData?.length == 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: "2rem" }}>
                  {t("dashboard.jobListings.table.emptyState")}
                </td>
              </tr>
            ) : (
              currentPageData?.map((job: any) => {
                const statusInfo = getJobStatus(job.status);
                const location =
                  job.city_name && job.country_name
                    ? `${job.city_name}, ${job.country_name}`
                    : job.city_name || job.country_name || t("dashboard.jobListings.table.locationNotSpecified");

                return (
                  <tr
                    key={job.emp_job_id || job.job_hash}
                    className="job-row clickable"
                    onClick={() => handleJobClick(job.emp_job_id || job.job_hash)}
                  >
                    <td>
                      <div>
                        <div className="job-title">
                          <Link
                            to={`/job-details/${job.emp_job_id}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: "inherit", textDecoration: "none" }}
                          >
                            {job.job_title}
                          </Link>
                        </div>
                        <div className="job-location">{location}</div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="job-date">{formatDate(job.posted_date)}</div>
                        <div className="job-expiry">{getDaysUntilExpiry(job.expiry_date)}</div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${statusInfo.class}`}>
                        <i className="status-dot"></i>
                        {statusInfo.text}
                      </span>
                    </td>
                    <td>
                      <span className="work-type-badge">
                        {job.workplace_type || t("dashboard.jobListings.table.workTypeNotSpecified")}
                      </span>
                    </td>
                    <td>
                      <div>
                        <span className="applicants-count">{job.screening_count?.screening_count || 0}</span>
                        {/* <span className="new-applicants">+ {job.newApplicants || 0} new</span> */}
                      </div>
                    </td>
                    <td>
                      <strong>{job.screening_count?.interview_count || 0}</strong>
                    </td>
                    <td>
                      <strong>{job.screening_count?.final_decision_count || 0}</strong>
                    </td>
                    <td>
                      <strong>{job.screening_count?.hired_count || 0}</strong>
                    </td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        {job.master_job_id ? (
                          <button
                            className="action-btn"
                            data-tooltip={t("dashboard.jobListings.table.actions.viewJob")}
                            onClick={() => {
                              if (job.master_job_id) {
                                const baseUrl = job.country_name === "UAE" ? EXTERNAL_AE_URL : EXTERNAL_URL;
                                const viewUrl = `${baseUrl}job/view/${job.master_job_id}`;
                                window.open(viewUrl, "_blank");
                              }
                            }}
                          >
                            <i className="fa fa-eye"></i>
                          </button>
                        ) : null}
                        {!job.master_job_id &&
                          // ((job.status === 10 && canEditAIPosting) || (job.status !== 10 && canEditJobPosting)) && (
                          job.status !== 10 &&
                          canEditJobPosting && (
                            <button
                              className="action-btn"
                              data-tooltip={t("dashboard.jobListings.table.actions.editJob")}
                              onClick={() => handleEditJob(job.emp_job_id, job.status)}
                            >
                              <i className="fa fa-edit"></i>
                            </button>
                          )}
                        {((job.status === 10 && canDeleteAIPosting) || (job.status !== 10 && canDeleteJobPosting)) && (
                          <button
                            className="action-btn"
                            data-tooltip={t("dashboard.jobListings.table.actions.stopJob")}
                            onClick={() => handleUpdateJobStatus(job.emp_job_id)}
                          >
                            <i className="fa fa-ban"></i>
                          </button>
                        )}
                        {!job.master_job_id && job.expiry_date && new Date(job.expiry_date) < new Date() && (
                          <button
                            className="action-btn"
                            data-tooltip={t("dashboard.jobListings.table.actions.repostJob")}
                            onClick={() => handleCloneJob(job.emp_job_id)}
                          >
                            <i className="fa fa-retweet"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {currentPageData?.length > 0 && totalPages > 1 ? (
        <div className="pagination">
          <button className="page-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
            {t("dashboard.jobListings.pagination.prev")}
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`}>...</span>
              ) : (
                <button
                  key={page}
                  className={`page-num ${currentPage === page ? "active" : ""}`}
                  onClick={() => handlePageChange(page as number)}
                >
                  {page}
                </button>
              )
            )}
          </div>
          <button className="page-btn" onClick={handleNextPage} disabled={currentPage === totalPages}>
            {t("dashboard.jobListings.pagination.next")}
          </button>
        </div>
      ) : null}

      {/* Clone Job Confirmation */}
      {showCloneConfirm && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText={t("dashboard.jobListings.cloneConfirm.confirmBtn")}
          confirmBtnBsStyle="preview"
          cancelBtnText={t("dashboard.jobListings.cloneConfirm.cancelBtn")}
          cancelBtnBsStyle="cancel"
          title={t("dashboard.jobListings.cloneConfirm.title")}
          onConfirm={confirmCloneJob}
          onCancel={cancelCloneJob}
        >
          {t("dashboard.jobListings.cloneConfirm.message")}
        </SweetAlert>
      )}

      {/* Update Job Status Confirmation */}
      {showUpdateStatusConfirm && (
        <SweetAlert
          warning
          showCancel
          confirmBtnText="Update Status"
          confirmBtnBsStyle="preview"
          cancelBtnText={t("dashboard.jobListings.cloneConfirm.cancelBtn")}
          cancelBtnBsStyle="cancel"
          title="Update Job Status"
          onConfirm={confirmUpdateJobStatus}
          onCancel={cancelUpdateJobStatus}
        >
          Are you sure you want to update this job status?
        </SweetAlert>
      )}
    </div>
  );
}
